// Биометрия во вкладке «Задания»: подпись итога шага в строке задачи, доступность
// «Повторить биометрию» и тексты массового перезапуска. Логика вне TasksManager.jsx, чтобы её
// можно было проверить без рендера.

export const BIOMETRY_RESTART_SCOPE = 'biometry';

const REASONS = {
  low_score: 'голос не похож ни на кого из сотрудников',
  low_margin: 'похожи сразу несколько сотрудников',
  insufficient_speech: 'оператор говорил слишком мало',
  no_speech: 'в записи нет речи',
  audio_unavailable: 'запись недоступна в хранилище',
  payload_too_large: 'запись слишком большая',
  audio_decode_error: 'запись не декодируется',
  empty_file: 'пустой файл',
  unprocessable: 'сервис не смог разобрать запись'
};

export const describeBiometryReason = (reason) => (reason ? REASONS[reason] ?? reason : null);

const formatScore = (score) => (Number.isFinite(score) ? score.toFixed(2) : null);

/**
 * Итог шага биометрии для строки задачи: { label, tone, title } или null, если шаг не выполнялся.
 * tone: 'ok' — сотрудник определён, 'warning' — расхождение с телефонией или порок записи,
 * 'muted' — голос не распознан.
 */
export const describeTaskBiometry = (metadata) => {
  const biometry = metadata?.biometry;
  if (!biometry) {
    return null;
  }

  const score = formatScore(biometry.score);
  const scoreTitle = score ? `сходство ${score}` : null;
  const model = biometry.model ? `модель ${biometry.model}` : null;
  const details = (...parts) => parts.filter(Boolean).join(', ');

  if (biometry.status === 'identified') {
    if (biometry.mismatch) {
      return {
        label: `По голосу: ${biometry.employeeName ?? 'другой сотрудник'}`,
        tone: 'warning',
        title: details('Телефония назвала другого сотрудника — он сохранён', scoreTitle, model)
      };
    }

    return {
      label: `По голосу: ${biometry.employeeName ?? 'сотрудник определён'}`,
      tone: 'ok',
      title: details('Сотрудник определён по голосу', scoreTitle, model)
    };
  }

  if (biometry.status === 'error') {
    return {
      label: 'Биометрия: запись не подошла',
      tone: 'warning',
      title: details(describeBiometryReason(biometry.reason), model)
    };
  }

  return {
    label: 'Голос не распознан',
    tone: 'muted',
    title: details(describeBiometryReason(biometry.reason), scoreTitle, model)
  };
};

/** «Повторить биометрию» — для завершённых и упавших задач при включённой биометрии. */
export const canRetryBiometry = (task, biometryEnabled) =>
  Boolean(biometryEnabled) && (task?.status === 'Completed' || task?.status === 'Failed');

/** Тексты подтверждения и результата массового перезапуска по виду операции. */
export const describeBulkRestart = (dialog) => {
  if (dialog?.scope === BIOMETRY_RESTART_SCOPE) {
    return {
      title: 'Повторить биометрию?',
      description:
        'Для завершённых и упавших заданий из текущего фильтра сотрудник будет заново определён по голосу. ' +
        'Распознавание речи и анализ не повторяются.',
      doneTitle: 'Повторная биометрия запущена'
    };
  }

  if (dialog?.fromCheckpoint) {
    return {
      title: 'Перезапустить с чекпоинта?',
      description: 'Все упавшие (Failed) задания из текущего фильтра будут возобновлены с последнего чекпоинта.',
      doneTitle: 'Перезапуск с чекпоинта запущен'
    };
  }

  return {
    title: 'Переобработать все задания?',
    description:
      'Все завершённые и упавшие задания из текущего фильтра будут поставлены в очередь на полную переобработку с нуля. ' +
      'Пропущенные задания сюда не попадают — чтобы обработать их, выберите в фильтре «Пропущенные».',
    doneTitle: 'Переобработка запущена'
  };
};

/**
 * Сообщение после массового перезапуска. Сервер только принимает выборку и перезапускает её в
 * фоне, поэтому сообщение обещает обновление списка, а не готовый итог: раньше перезапуск шёл в
 * самом запросе, обрывался по таймауту, и хвост выборки молча оставался неперезапущенным.
 */
export const describeBulkRestartResult = (dialog, result) => {
  const accepted = Number(result?.accepted) || 0;
  const total = Number(result?.total) || 0;
  if (accepted === 0) {
    return {
      title: 'Перезапускать нечего',
      description: 'В текущем фильтре нет заданий в подходящем статусе.'
    };
  }

  const unsuitable = total - accepted;
  return {
    title: describeBulkRestart(dialog).doneTitle,
    description:
      `Взято в работу: ${accepted}. Задания перезапускаются в фоне — статусы в списке обновятся по мере обработки.` +
      (unsuitable > 0 ? ` Не подошли по статусу: ${unsuitable}.` : '')
  };
};
